import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { FormikUploadField } from 'components/FormikUploadField/FormikUploadField';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';

interface UploadLicenseForm {
  file?: File[];
}

export const IpfCertificateUpload = () => {
  const api = useApi();
  const { showNotification } = useNotifications();
  const [loadingState, setLoadingState] = useLoadingState();
  const [error, setError] = useState<string>('');

  const clearCallbackRef = useRef<() => void>();

  const handleClear = () => {
    clearCallbackRef.current?.();
  };

  const uploadCertificate = async (file: File) => {
    const formData = new FormData();
    formData.append('certificate', file);

    setError('');
    try {
      await api.request({
        url: '/os/upload-nimpee-cert',
        data: formData,
        formData: true,
        method: 'POST',
      });

      showNotification({
        message: 'IP Fabric certificate uploaded successfully',
        type: 'success',
      });

      handleClear();
    } catch (error) {
      if (isIpfError(error)) {
        setError(error.message);
      }

      throw error;
    }
  };

  useEffect(() => {
    formik.setFieldError('file', error);
  }, [error]);

  const formik = useFormik<UploadLicenseForm>({
    initialValues: {
      file: undefined,
    },
    onSubmit: (values) => {
      if (values.file?.length) {
        // Fixed noUncheckedIndexedAccess NIM-19408
        const selectedFile = values.file[0]!;

        withLoadingIndicator(setLoadingState)(async () => {
          await uploadCertificate(selectedFile);
        });
      }
    },
    enableReinitialize: true,
  });

  return (
    <div className='m-4'>
      <Helmet title='IP Fabric Certificate Upload' />
      <Card>
        <div className='p text-level-5 font-semibold'>
          IP Fabric Certificate Upload
        </div>

        <form onSubmit={formik.handleSubmit} className='px'>
          <FormikUploadField
            name='file'
            multiple={false}
            label='Only PEM (Base 64 encoded) is supported'
            formik={formik}
            onClear={(clear) => {
              clearCallbackRef.current = clear;
            }}
            onChange={() => formik.handleSubmit()}
            loading={loadingState === 'LOADING'}
          />
        </form>
      </Card>
    </div>
  );
};
